import React from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import AuthProvider from "./auth/AuthProvider.jsx";
import ProtectedRoute from "./auth/ProtectedRoute.jsx";
import Landing from "./Landing.jsx";
import LandingPage from "./LandingPage.jsx";
import PricingPage from "./tattoo/billing/PricingPage.jsx";
import TattooApp from "./tattoo/TattooApp.jsx";
import UsernameSetupPage from "./auth/UsernameSetupPage.jsx";
import AuthCallback from "./auth/AuthCallback.jsx";
import "./styles.css";
import { initSentry, SentryErrorBoundary } from "./sentry";

// Boot Sentry as early as possible — before any auth bootstrap, before
// React renders. initSentry() no-ops if VITE_SENTRY_DSN is missing, so
// this is safe to leave on always (dev runs without env vars stay quiet).
initSentry();

// 🚧 DEBUG MARKER — bump the version whenever you push a debug build.
// If you don't see this in the prod console, the deploy hasn't landed
// yet OR console.* is being stripped somewhere. Remove after phantom
// hunt is done.
console.warn("[TATTUNE-DEBUG] boot marker v1 — if you see this, console.warn survives to prod");

// 🚧 DEBUG TRAP v2 — logs EVERY relevant localStorage write during
// boot, so we can see the full sequence that leads to a phantom being
// created. Filters out Supabase auth token writes to keep noise down.
try {
  const origSet = Storage.prototype.setItem;
  Storage.prototype.setItem = function (key, value) {
    if (typeof key === "string" && !key.startsWith("sb-")) {
      const preview =
        typeof value === "string"
          ? value.length > 220
            ? value.slice(0, 220) + "…"
            : value
          : String(value);
      console.warn("[TATTUNE-TRAP] SET", { key, len: (value || "").length });
      console.warn("[TATTUNE-TRAP] value:", preview);
      console.trace("[TATTUNE-TRAP] caller");
    }
    return origSet.apply(this, arguments);
  };
  console.warn("[TATTUNE-TRAP v2] installed at boot — logging ALL non-auth localStorage writes");
} catch (e) {
  console.warn("[TATTUNE-TRAP] install failed:", e);
}

// 🚧 Also trap Object mutation on the tattoo store's projectId so we
// can see EXACTLY who assigns it during boot. This uses a Proxy
// intercept on a globally-accessible marker for later.
if (typeof window !== "undefined") {
  window.__TATTUNE_TRAP_PROJECT_ID = (id, source) => {
    console.warn("[TATTUNE-TRAP] tattoo.projectId set", { id, source });
    console.trace("[TATTUNE-TRAP] projectId assignment");
  };
}

import { maybeLoadSharedProject } from "./tattoo/share";
import { commentsStore } from "./tattoo/commentsState";
import { fetchSharedCommentsForCurrentProject } from "./tattoo/sharedCommentsApi";
import LeadView from "./tattoo/LeadView.jsx";
import ArtistPublicPage from "./tattoo/ArtistPublicPage.jsx"; // 👈 NEW
import BrowseArtistsPage from "./tattoo/BrowseArtistsPage.jsx";
import Viewer from "./routes/Viewer.jsx";
import BillingPage from "./tattoo/billing/BillingPage.jsx";
import GalleryPage from "./tattoo/gallery/GalleryPage.jsx";
import FlashManagementPage from "./tattoo/flash/FlashManagementPage.jsx";
import InboxPage from "./tattoo/inbox/InboxPage.jsx";
import LeadDetailPage from "./tattoo/inbox/LeadDetailPage.jsx";
import PoolDetailPage from "./tattoo/inbox/PoolDetailPage.jsx";
import PrivacyPage from "./tattoo/legal/PrivacyPage.jsx";

// Tiny inline wrapper for shared links
function ViewGate() {
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      console.log("[share] boot: calling maybeLoadSharedProject()");
      await maybeLoadSharedProject();

      // 🔹 Load shared comments for this share key (if any)
      try {
        const threads = await fetchSharedCommentsForCurrentProject();
        if (threads) {
          commentsStore.threads = threads;
          console.log("[share] hydrated shared comments", threads);
        }
      } catch (e) {
        console.warn("[share] failed to hydrate shared comments:", e);
      }

      setReady(true);
    })();
  }, []);

  if (!ready) return null; // can swap for a spinner later if you want

  // ⬇️ use Viewer, which sets readOnly + feedback UI
  return <Viewer />;
}

const router = createBrowserRouter([
  { path: "/", element: <LandingPage /> },           // public marketing homepage
  { path: "/signin", element: <Landing /> },         // magic-link sign-in form
  { path: "/pricing", element: <PricingPage /> },    // public pricing page
  { path: "/view", element: <ViewGate /> },          // shared review link
  { path: "/lead/:slug", element: <LeadView /> },    // lead preview links
  { path: "/artists", element: <BrowseArtistsPage /> },     // public browse index
  { path: "/artists/:slug", element: <ArtistPublicPage /> }, // public artist page
  { path: "/privacy", element: <PrivacyPage /> }, // public — must be reachable without sign-in
  { path: "/auth/callback", element: <AuthCallback /> },
  {
    path: "/setup",
    element: (
      <ProtectedRoute allowSetup>
        <UsernameSetupPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/app",
    element: (
      <ProtectedRoute>
        <TattooApp />
      </ProtectedRoute>
    ),
  },
  {
    path: "/account/billing",
    element: (
      <ProtectedRoute>
        <BillingPage />
      </ProtectedRoute>
    ),
  },
  {
    // Old standalone gallery route — kept as a redirect to the
    // combined Studio profile page so old in-app links and bookmarks
    // continue to land somewhere useful.
    path: "/account/gallery",
    element: <Navigate to="/account/studio" replace />,
  },
  {
    // Old flash-only route — same redirect treatment. The combined
    // Studio profile page now handles logo + studio gallery + flash
    // under a single URL so artists aren't hopping pages.
    path: "/account/flash",
    element: <Navigate to="/account/studio" replace />,
  },
  {
    path: "/account/studio",
    element: (
      <ProtectedRoute>
        <FlashManagementPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/inbox",
    element: (
      <ProtectedRoute>
        <InboxPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/inbox/lead/:id",
    element: (
      <ProtectedRoute>
        <LeadDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/inbox/pool/:id",
    element: (
      <ProtectedRoute>
        <PoolDetailPage />
      </ProtectedRoute>
    ),
  },
]);

// Last-resort fallback when a React render breaks the whole tree.
// Sentry has already captured the error before this renders.
function ErrorFallback() {
  return (
    <div
      style={{
        padding: 40,
        color: "#fff",
        background: "#1a1a1a",
        minHeight: "100vh",
        fontFamily: "system-ui, -apple-system, sans-serif",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        textAlign: "center",
      }}
    >
      <h1 style={{ fontSize: 28, margin: 0 }}>Something went wrong.</h1>
      <p style={{ margin: 0, color: "rgba(255,255,255,0.7)", maxWidth: 420 }}>
        The error has been logged. Try refreshing the page.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: "10px 20px",
          borderRadius: 8,
          background: "#a2332b",
          color: "#fff",
          border: "none",
          cursor: "pointer",
          fontSize: 14,
          fontWeight: 500,
        }}
      >
        Refresh
      </button>
    </div>
  );
}

createRoot(document.getElementById("root")).render(
  <SentryErrorBoundary fallback={<ErrorFallback />}>
    <AuthProvider>
      <RouterProvider router={router} />
      <Toaster
        position="bottom-center"
        containerStyle={{ zIndex: 100000 }}
        toastOptions={{
          duration: 3500,
          style: {
            background: "#1f2937",
            color: "#f9fafb",
            fontSize: "14px",
            borderRadius: "8px",
            padding: "10px 14px",
          },
          success: { iconTheme: { primary: "#10b981", secondary: "#f9fafb" } },
          error: { iconTheme: { primary: "#ef4444", secondary: "#f9fafb" } },
        }}
      />
    </AuthProvider>
  </SentryErrorBoundary>
);